import React, { useEffect, useState } from 'react';
import { Check, Gift, ShoppingBag, X, Clock } from 'lucide-react';
import { Drop } from '../../types';
import { QRCodeDisplay } from '../QRCodeDisplay';
import { triggerConfetti } from '../ui/ConfettiButton';
interface DropUnlockedScreenProps {
  drop: Drop;
  dropCode: string;
  onApplyToOrder: () => void;
  onSendAsGift: () => void;
  onClose: () => void;
}
export function DropUnlockedScreen({
  drop,
  dropCode,
  onApplyToOrder,
  onSendAsGift,
  onClose
}: DropUnlockedScreenProps) {
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes in seconds
  useEffect(() => {
    // Trigger confetti on mount
    triggerConfetti();
    // Countdown timer
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="min-h-screen bg-gray-50 pb-24" data-id="element-653">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 px-5 py-8 relative overflow-hidden" data-id="element-654">
        <button onClick={onClose} className="absolute top-6 right-5 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors" data-id="element-655">
          <X size={20} className="text-white" data-id="element-656" />
        </button>

        <div className="text-center" data-id="element-657">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-658">
            <Check size={40} className="text-green-500" strokeWidth={3} data-id="element-659" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" data-id="element-660">Drop Unlocked!</h1>
          <p className="text-green-100 text-lg" data-id="element-661">{drop.itemName}</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-6 space-y-6" data-id="element-662">
        {/* Timer */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-id="element-663">
          <div className="flex items-center justify-between" data-id="element-664">
            <div className="flex items-center gap-2" data-id="element-665">
              <Clock size={20} className="text-amber-600" data-id="element-666" />
              <span className="font-medium text-gray-900" data-id="element-667">Valid for</span>
            </div>
            <span className="text-2xl font-bold text-amber-600" data-id="element-668">
              {formatTime(timeRemaining)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-2" data-id="element-669">
            Use this drop within the next 30 minutes
          </p>
        </div>

        {/* Drop Code */}
        <div className="bg-white rounded-2xl p-6 shadow-sm text-center" data-id="element-670">
          <h3 className="font-bold text-gray-900 mb-4" data-id="element-671">Your Drop Code</h3>

          <div className="inline-block bg-gray-50 rounded-xl p-6 mb-4" data-id="element-672">
            <QRCodeDisplay value={dropCode} size={180} data-id="element-673" />
          </div>

          <div className="bg-purple-100 rounded-xl p-4 mb-2" data-id="element-674">
            <p className="text-3xl font-bold text-purple-900 tracking-wider" data-id="element-675">
              {dropCode}
            </p>
          </div>

          <p className="text-sm text-gray-500" data-id="element-676">
            Show this code to staff at any participating store
          </p>
        </div>

        {/* Brand Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-id="element-677">
          <div className="flex items-center gap-3" data-id="element-678">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-679">
              <img src={drop.brandLogo} alt={drop.brandName} className="w-full h-full object-cover" data-id="element-680" />
            </div>
            <div className="flex-1" data-id="element-681">
              <p className="font-medium text-gray-900" data-id="element-682">{drop.brandName}</p>
              <p className="text-sm text-gray-500" data-id="element-683">Worth ₹{drop.value}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3" data-id="element-684">
          <h3 className="font-bold text-gray-900" data-id="element-685">
            What would you like to do?
          </h3>

          <button onClick={onApplyToOrder} className="w-full bg-[#FFFC00] text-black p-5 rounded-xl hover:bg-[#E6E300] transition-all active:scale-[0.98] text-left" data-id="element-686">
            <div className="flex items-center gap-4" data-id="element-687">
              <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center" data-id="element-688">
                <ShoppingBag size={24} data-id="element-689" />
              </div>
              <div className="flex-1" data-id="element-690">
                <h4 className="font-bold text-lg" data-id="element-691">Apply to My Order</h4>
                <p className="text-sm text-gray-700" data-id="element-692">
                  Add to cart and order now
                </p>
              </div>
              <div className="text-2xl" data-id="element-693">→</div>
            </div>
          </button>

          <button onClick={onSendAsGift} className="w-full bg-white border-2 border-gray-200 text-black p-5 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all active:scale-[0.98] text-left" data-id="element-694">
            <div className="flex items-center gap-4" data-id="element-695">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-696">
                <Gift size={24} className="text-purple-600" data-id="element-697" />
              </div>
              <div className="flex-1" data-id="element-698">
                <h4 className="font-bold text-lg" data-id="element-699">Send as Gift</h4>
                <p className="text-sm text-gray-600" data-id="element-700">Share with a friend</p>
              </div>
              <div className="text-2xl text-gray-400" data-id="element-701">→</div>
            </div>
          </button>
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 rounded-2xl p-4" data-id="element-702">
          <h4 className="font-bold text-gray-900 mb-2" data-id="element-703">How to use:</h4>
          <ol className="space-y-2 text-sm text-gray-600" data-id="element-704">
            <li className="flex gap-2" data-id="element-705">
              <span className="font-bold" data-id="element-706">1.</span>
              <span data-id="element-707">Visit any participating {drop.brandName} store</span>
            </li>
            <li className="flex gap-2" data-id="element-708">
              <span className="font-bold" data-id="element-709">2.</span>
              <span data-id="element-710">Show this code to staff at checkout</span>
            </li>
            <li className="flex gap-2" data-id="element-711">
              <span className="font-bold" data-id="element-712">3.</span>
              <span data-id="element-713">Enjoy your free {drop.itemName}!</span>
            </li>
          </ol>
        </div>
      </div>

      {/* Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-5 max-w-md mx-auto" data-id="element-714">
        <button onClick={onClose} className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl font-bold text-lg hover:bg-gray-200 transition-all" data-id="element-715">
          Done
        </button>
      </div>
    </div>;
}